import type { ComponentRef, ViewContainerRef } from '@angular/core';
import { inject } from '@angular/core';
import type { ValidationErrors } from '@angular/forms';
import { FormControl, Validators } from '@angular/forms';

import type { Observable } from 'rxjs';
import { map, takeUntil, combineLatest, startWith, asapScheduler, debounceTime } from 'rxjs';

import { setInputs } from '@core/common-legacy';
import { getFilterOptionsId } from '@core/views-data';
import type { ViewFilterInput, ViewFilterInputDef } from '@core/views-types';

import { FiltersFormStateService } from '../../services/filters-form-state.service';
import { OptionsService } from '../options.service';
import type { InputComponentContext, InputService } from '../types';
import { FilterEvents } from '../types';
import { viewsInputFormatDate } from './date-range-picker/date-range-picker.component';
import type { SelectComponentState } from './input-select.component';
import { InputSelectComponent, optionToSelectItem } from './input-select.component';
import type { InputSelectFormValue, InputSelectOptions, InputSelectSavedValue } from './types';

export class InputSelectService implements InputService {
  private optionsService = inject(OptionsService);
  private filterStateService = inject(FiltersFormStateService);

  public savedToFormValue(input: ViewFilterInput<InputSelectSavedValue>): InputSelectFormValue {
    return <InputSelectFormValue>{
      ...input.value.value,
      type: input.value.type,
    };
  }

  public getComponentState(input: ViewFilterInput<InputSelectSavedValue>) {
    return optionToSelectItem(input.value);
  }

  public getInputControl(inputDef: ViewFilterInputDef): InputSelectComponent['inputControl'] {
    /*
     * This validator is used for date select
     */
    const allKeysValue = ({ value }: FormControl): ValidationErrors | null => {
      if (!value) return null;

      const selectedValue: InputSelectFormValue = value;
      const missingKeys = Object.entries(selectedValue)
        .filter(([k, v]) => !v)
        .map(([k]) => [k, true]);
      return missingKeys.length ? Object.fromEntries(missingKeys) : null;
    };
    const validators = Validators.compose([
      inputDef.required ? Validators.required : null,
      allKeysValue,
    ]);
    return new FormControl(null, validators);
  }

  public createInputComponent(
    vcRef: ViewContainerRef,
    destroy$: Observable<void>,
    context: InputComponentContext,
  ): ComponentRef<InputSelectComponent> {
    const inputOptionsId = getFilterOptionsId(
      context.dataSourceId,
      context.filterDef.id,
      context.inputDef.id,
      context.uuid,
    );
    const componentRef = vcRef.createComponent(InputSelectComponent);
    const mode = context.origin === 'quick' && context.inputs.length === 1 ? 'quick' : 'advanced';
    this.updateInputs(componentRef, { mode, placeholder: context.inputDef.props.placeholder });

    const subscriptions$ = this.optionsService.getSubscriptionsChanges(context, destroy$);
    this.optionsService.handleLoadOptions(
      context,
      destroy$,
      subscriptions$,
      componentRef.instance.search,
    );
    this.optionsService.handleLoadMore(
      context,
      destroy$,
      subscriptions$,
      componentRef.instance.loadMore,
    );
    this.watchOptions(inputOptionsId, destroy$, componentRef);
    this.publishFilterEvents(context, destroy$, componentRef.instance);
    this.validateOptions(context, destroy$);

    return componentRef;
  }

  public getInputLabel(
    context: InputComponentContext,
    control: InputSelectComponent['inputControl'],
    inputState$: Observable<SelectComponentState>,
  ) {
    const inputOptionsId = this.optionsService.getInputOptionsId(context);
    return combineLatest([
      this.optionsService
        .getOptions<InputSelectOptions>(inputOptionsId)
        .pipe(map(response => response?.options ?? [])),
      control.valueChanges.pipe(startWith(control.value)),
      inputState$,
    ]).pipe(
      debounceTime(0, asapScheduler),
      map(([options, controlValue, inputState]) => {
        if (!controlValue) return '';

        if (controlValue.type === 'date') {
          return viewsInputFormatDate(controlValue.date);
        } else if (controlValue.type === 'date_within') {
          return `${controlValue.modifier} ${controlValue.amount} ${controlValue.unit}`;
        } else if (controlValue.type === 'date_range') {
          return viewsInputFormatDate([controlValue.from, controlValue.to]);
        }

        return inputState?.label ?? '';
      }),
    );
  }

  public updateInputs(
    componentRef: ComponentRef<InputSelectComponent>,
    inputs: Partial<
      Pick<InputSelectComponent, 'inputControl' | 'state' | 'optionsData' | 'mode' | 'placeholder'>
    >,
  ) {
    setInputs(componentRef, inputs);
    componentRef.changeDetectorRef.detectChanges();
  }

  private publishFilterEvents(
    context: InputComponentContext,
    destroy$: Observable<void>,
    component: InputSelectComponent,
  ) {
    component.closed.pipe(takeUntil(destroy$)).subscribe(() => {
      context.filterEvents.next({ inputId: context.inputDef.id, event: FilterEvents.InputBlur });
    });
  }

  private watchOptions(
    inputOptionsId: string,
    destroy$: Observable<void>,
    componentRef: ComponentRef<InputSelectComponent>,
  ) {
    this.optionsService
      .getOptions<InputSelectOptions>(inputOptionsId)
      .pipe(takeUntil(destroy$))
      .subscribe((options: InputSelectComponent['optionsData']) =>
        this.updateInputs(componentRef, { optionsData: options }),
      );
  }

  private validateOptions(context: InputComponentContext, destroy$: Observable<void>) {
    const filterControl = this.filterStateService.getFilterControl(context.uuid)!;
    const inputIndex = this.optionsService.getInputIndex(context.inputs, context.inputDef.id);
    const inputControl = <InputSelectComponent['inputControl']>(
      filterControl.controls.value.controls[inputIndex]
    );
    this.optionsService
      .validateOptions<InputSelectComponent, InputSelectOptions>(
        context,
        filterControl,
        inputControl => !!inputControl.value,
      )
      .pipe(takeUntil(destroy$))
      .subscribe(([option]) => {
        const state = option ? optionToSelectItem(option) : null;
        const { inputState } = this.filterStateService.getFilterData(filterControl);
        const newState = [...inputState];
        newState.splice(inputIndex, 1, state);
        inputControl.setValue(state?.value ?? null);
        this.filterStateService.updateFilterState(context.uuid, newState);
      });
  }
}
